import classNames from "classnames";
import React from "react";
import styles from "./styles.module.scss";

const flexSpaceBetween = {
  display: "flex",
  justifyContent: "space-between",
};

interface ActivityItemProps {
  item: {
    id: string;
    actionTitle: string;
    actionDescription: string;
    actor: string;
    amount?: number;
    timestamp: string;
    unread: boolean;
    contractName?: string;
  };
  onClick: () => void;
}

const timeAgo = (timestamp: string): string => {
  let past: Date;
  try {
    past = new Date(timestamp);
    if (isNaN(past.getTime())) throw new Error("Invalid date");
  } catch {
    // fallback: return raw string if invalid
    return timestamp;
  }

  const now = new Date();
  const diff = Math.floor((now.getTime() - past.getTime()) / 1000); // seconds

  const arabicUnits = {
    minute: { singular: "دقيقة", dual: "دقيقتين", plural: "دقيقة" },
    hour: { singular: "ساعة", dual: "ساعتين", plural: "ساعات" },
    day: { singular: "يوم", dual: "يومين", plural: "أيام" },
  };

  const formatUnit = (
    count: number,
    unit: keyof typeof arabicUnits
  ): string => {
    const units = arabicUnits[unit];

    if (count === 1) return `منذ ${count} ${units.singular}`;
    if (count === 2) return `منذ ${count} ${units.dual}`;

    // Arabic pluralization rules are complex. For 3-10, it's typically plural (جَمْع قِلّة).
    // For simplicity, we use the general plural form for counts > 2,
    // although standard Arabic grammar applies the plural for 3 to 10 and then singular for 11 and 12, etc.
    // Sticking to the most common digital formatting practice:
    return `منذ ${count} ${units.plural}`;
  };

  if (diff < 60) {
    return `منذ ${diff} ثواني`; // Using ثواني (plural) as seconds are usually > 1
  }

  const minutes = Math.floor(diff / 60);
  if (diff < 3600) {
    return formatUnit(minutes, "minute");
  }

  const hours = Math.floor(diff / 3600);
  if (diff < 86400) {
    return formatUnit(hours, "hour");
  }

  const days = Math.floor(diff / 86400);
  if (diff < 604800) {
    return formatUnit(days, "day");
  }

  // Fallback: full Gregorian date in Arabic
  return new Intl.DateTimeFormat("ar-SA-u-ca-gregory", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(past);
};

// Determine color for action words
const getActionColor = (actionText: string): string => {
  const text = actionText.toLowerCase();

  // --- Red (Rejection/Denial) ---
  // Covers: تم رفض..., CUSTOMER_DENY_DELIVERY
  if (text.includes("رفض") || text.includes("deny")) {
    return "#E32A34";
  }

  // --- Green (Payment/Acceptance/Joining) ---
  // Covers: تم دفع..., تم إيداع..., تم تأكيد..., تم قبول..., RECEIVER_ACCEPT_CONTRACT
  if (
    text.includes("دفع") ||
    text.includes("إيداع") ||
    text.includes("قبول") ||
    text.includes("تأكيد") ||
    text.includes("paid") ||
    text.includes("accept")
  ) {
    return "#3ECC85";
  }

  // --- Orange (Escalation/Change/Execution/Other) ---
  // Covers: قام بتصعيد..., قام بتأكيد التنفيذ, قام بإنشاء...
  if (
    text.includes("تصعيد") ||
    text.includes("escalate") ||
    text.includes("تسوية") ||
    text.includes("فحص")
  ) {
    return "#D18B49";
  }

  // Fallback for any unmapped or neutral action
  return "#181B23";
};

// Render description with actor name and colored keywords
const renderColoredDescription = (description: string, actor: string) => {
  const words = description.split(/(\s+)/); // keep spaces
  return words.map((word, i) => {
    if (actor?.includes(word))
      return (
        <span key={i} style={{ color: "#0D44AD" }}>
          {word}
        </span>
      );
    const color = getActionColor(word);
    if (color !== "#181B23")
      return (
        <span key={i} style={{ color }}>
          {word}
        </span>
      );
    return <span key={i}>{word}</span>;
  });
};

const ActivityItem: React.FC<ActivityItemProps> = ({ item, onClick }) => {
  const { actionDescription, timestamp, unread, contractName, actor } = item;

  return (
    <div
      className={classNames(styles.updatesListItem, {
        [styles.updatesListItemRead]: !unread,
      })}
      onClick={onClick}>
      <div className={styles.updatesItemContainer}></div>
      <div className={styles.updatesItemContent}>
        <div style={flexSpaceBetween}>
          <div className={styles.updatesActionTitle}>
            {renderColoredDescription(actionDescription, actor)}
          </div>
          <div className={styles.updatesItemLeftIndicator}>
            {unread && <div className={styles.updatesUnreadIndicator}></div>}
          </div>
        </div>
        <div style={flexSpaceBetween}>
          <div className={styles.updatesActionDescription}>{contractName}</div>
          <div className={styles.updatesItemFooter}>
            <div className={styles.updatesTimestamp}>{timeAgo(timestamp)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityItem;
